import React, { useEffect, useMemo, useState } from "react";
import ScatterPlotExpression from "./ScatterPlotExpression";
import analysisService from "../../services/analysis.service";
import { useProjectStore } from "../../stores/project.store";
import { Autocomplete, Box, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

export interface ClusterScatterPlotProps {
  data: any[];
  xMetric?: string;
  yMetric?: string;
  groupKey?: string;
  title?: string;
  className?: string;
  plotRef?: string;
  genesList?: string[];
}

const ClusterScatterPlot = ({
  data,
  xMetric = "UMAP1",
  yMetric = "UMAP2",
  groupKey,
  title = "UMAP Clustering",
  className = "",
  plotRef,
  genesList = [],
}: ClusterScatterPlotProps): React.ReactElement => {
  const projectName = useProjectStore((state) => state.analysisData?.projectName);
  const storeGenes = useProjectStore((state) => state.backendResult?.genesList);
  const globalSelectedGene = useProjectStore((state) => state.selectedGene);
  const setGlobalSelectedGene = useProjectStore((state) => state.setSelectedGene);
  const setBackendResult = useProjectStore((state) => state.setBackendResult);
  const saveStepResult = useProjectStore((state) => state.saveStepResult);
  const activeStep = useProjectStore((state) => state.activeStep);
  const stepResults = useProjectStore((state) => state.stepResults);
  const availableGenes: string[] = useMemo(
    () => (Array.isArray(genesList) && genesList.length ? genesList : (Array.isArray(storeGenes) ? storeGenes : [])),
    [genesList, storeGenes]
  );
  const [displayMode, setDisplayMode] = useState<"cluster" | "gene">("cluster");
  const [selectedGene, setSelectedGene] = useState<string | null>(
    availableGenes.includes(globalSelectedGene) ? globalSelectedGene : (availableGenes[0] ?? null),
  );
  const [geneRows, setGeneRows] = useState<any[]>([]);
  const [geneLoading, setGeneLoading] = useState(false);
  const [geneError, setGeneError] = useState<string | null>(null);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [groupLabels, setGroupLabels] = useState<Record<string, string>>({});
  const projectKey = `survom-cluster-labels:${projectName ?? "unknown"}`;
  const metadataKeys = useMemo(() => {
    if (!data?.length) return [];
    return Object.keys(data[0]).filter((key) => {
      const value = data[0][key];
      if (key === xMetric || key === yMetric || key === "barcode" || typeof value === "object") {
        return false;
      }
      const uniqueCount = new Set(data.map((row) => String(row[key]))).size;
      return (key === groupKey || uniqueCount > 1) && uniqueCount <= 50;
    });
  }, [data, xMetric, yMetric, groupKey]);
  const defaultGroupKey =
    groupKey || (metadataKeys.includes("leiden") ? "leiden" : metadataKeys[0] || "cluster");
  const [selectedGroupKey, setSelectedGroupKey] = useState(defaultGroupKey);
  useEffect(() => {
    if (!metadataKeys.includes(selectedGroupKey)) {
      setSelectedGroupKey(
        metadataKeys.includes("leiden")
          ? "leiden"
          : metadataKeys.includes("cluster")
            ? "cluster"
            : metadataKeys[0] || "",
      );
    }
  }, [metadataKeys, selectedGroupKey]);

  useEffect(() => {
    if (availableGenes.includes(globalSelectedGene) && globalSelectedGene !== selectedGene) {
      setSelectedGene(globalSelectedGene);
    } else if ((!selectedGene || !availableGenes.includes(selectedGene)) && availableGenes.length) {
      setSelectedGene(availableGenes[0]);
    }
  }, [availableGenes, globalSelectedGene, selectedGene]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(projectKey) || "{}");
      if (saved && typeof saved === "object") setGroupLabels(saved);
    } catch { /* use defaults */ }
  }, [projectKey]);

  const updateGroupLabel = (group: string, label: string) => {
    setGroupLabels((previous) => {
      const next = { ...previous, [group]: label };
      localStorage.setItem(projectKey, JSON.stringify(next));
      return next;
    });
  };

  const loadGene = async (gene: string | null) => {
    if (!gene || !projectName) return;
    setDisplayMode("gene");
    setSelectedGene(gene);
    setGlobalSelectedGene(gene);
    setGeneLoading(true);
    setGeneError(null);
    try {
      const result = await analysisService.fetchGeneExpression(projectName, gene, activeStep ?? "Clustering");
      if (result.status !== "success") throw new Error(result.message || "Unable to load gene expression");
      const responseRows = Array.isArray(result.tableData) ? result.tableData : [];
      const expressionGene = result.selectedGene ?? gene;
      const nextRows = responseRows.map((row: any) => ({ ...row, expression: row.expr ?? row[expressionGene] ?? 0 }));
      setGeneRows(nextRows);
      const updates = {
        selectedGene: expressionGene,
        tableData: nextRows,
        umapData: nextRows,
        genesList: result.genesList,
        artifactId: result.artifactId,
        artifactStep: result.artifactStep,
      };
      const currentResult = useProjectStore.getState().backendResult || {};
      setBackendResult({ ...currentResult, ...updates });
      if (activeStep) {
        const currentStepResult = stepResults[activeStep] || {};
        saveStepResult(activeStep, { ...currentStepResult, ...updates });
      }
    } catch (error) {
      setGeneError(error instanceof Error ? error.message : "Unable to load gene expression");
    } finally {
      setGeneLoading(false);
    }
  };

  const plottedData = displayMode === "gene" && geneRows.length ? geneRows : data;
  const plotTitle = displayMode === "gene" ? "UMAP Gene Expression — " + (selectedGene || "Selected gene") : title;
  const plottedGroupLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    Object.keys(groupLabels).forEach((key) => {
      const prefix = `${selectedGroupKey}:`;
      if (key.startsWith(prefix)) labels[key.slice(prefix.length)] = groupLabels[key];
    });
    return labels;
  }, [groupLabels, selectedGroupKey]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <Paper variant="outlined" sx={{ p: 1.5, borderColor: "#90caf9", backgroundColor: "#f7fbff" }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Color UMAP</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5 }}>
          <ToggleButtonGroup size="small" exclusive value={displayMode} onChange={(_event, mode) => { if (mode === "cluster") setDisplayMode("cluster"); if (mode === "gene") selectedGene ? void loadGene(selectedGene) : setDisplayMode("gene"); }}>
            <ToggleButton value="cluster">Clusters / metadata</ToggleButton>
            <ToggleButton value="gene">Gene expression</ToggleButton>
          </ToggleButtonGroup>
          {selectedGene && <Typography variant="body2" sx={{ alignSelf: "center", color: "#1565c0", fontWeight: 600 }}>Selected gene: {selectedGene}</Typography>}
          <Autocomplete
            size="small"
            options={availableGenes}
            value={selectedGene}
            onChange={(_event, value) => loadGene(value)}
            sx={{ width: 300 }}
            loading={geneLoading}
            disabled={!availableGenes.length}
            renderInput={(params) => <TextField {...params} label="Search gene to color UMAP" helperText={geneError ?? "Selecting a gene switches to expression coloring."} InputProps={{ ...params.InputProps, endAdornment: <>{geneLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }} />}
          />
        </Box>
      </Paper>
      {displayMode === "cluster" && (
        <FormControl size="small" sx={{ width: 280, alignSelf: "flex-start" }}>
          <InputLabel id="metadata-group-label">Color/group by</InputLabel>
          <Select
            labelId="metadata-group-label"
            value={selectedGroupKey}
            label="Color/group by"
            onChange={(event) => setSelectedGroupKey(event.target.value)}
          >
            {metadataKeys.map((key) => (
              <MenuItem key={key} value={key}>
                {key === "leiden" ? "Leiden cluster" : key === "cluster" ? "Cluster" : key}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {displayMode === "cluster" && (
        <Button size="small" variant="outlined" onClick={() => setLabelsOpen(true)} sx={{ alignSelf: "flex-start" }}>
          Edit cluster names
        </Button>
      )}
      <div className="min-h-0 flex-1">
        <ScatterPlotExpression
          data={plottedData}
          xMetric={xMetric}
          yMetric={yMetric}
          groupKey={displayMode === "cluster" ? selectedGroupKey : undefined}
          colorMetric={displayMode === "gene" ? "expression" : undefined}
          title={plotTitle}
          className={className}
          plotRef={plotRef}
          defaultSize={selectedGroupKey === "annotation" ? 4 : 7}
          showSizeSlider={true}
          showLegend={true}
          groupLabels={plottedGroupLabels}
        />
      </div>
      <Dialog open={labelsOpen} onClose={() => setLabelsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit group names</DialogTitle>
        <DialogContent dividers>
          <div className="space-y-3 pt-1">
            {metadataKeys.length === 0 ? <div>No metadata groups available.</div> : metadataKeys.map((key) => {
              const groups = [...new Set(data.map((row) => String(row[key])))].sort();
              return <div key={key} className="space-y-2"><strong>{key}</strong>{groups.map((group) => (
                <TextField key={`${key}-${group}`} fullWidth size="small" label={`${key}: ${group}`} value={groupLabels[`${key}:${group}`] ?? ""} placeholder={group} onChange={(event) => updateGroupLabel(`${key}:${group}`, event.target.value)} />
              ))}</div>;
            })}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGroupLabels({}); localStorage.removeItem(projectKey); }}>Reset</Button>
          <Button onClick={() => setLabelsOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClusterScatterPlot;
